// Order Summary - Sticky Bottom Bar (Kustom Style)

import { useState } from 'react';
import { useCheckoutStore, useTotalWithShipping } from '../store/checkout';
import styles from './OrderSummary.module.css';

// Payment method logos in correct order
const PAYMENT_METHOD_LOGOS: Record<string, { url: string; alt: string; methods: string[] }> = {
  visa: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765667/surfboardmerchant/hpxkzcghdvo2duf4a9cm.png',
    alt: 'Visa',
    methods: ['CARD', 'VISA'],
  },
  mastercard: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765654/surfboardmerchant/e4knt66x4mjaspstalbn.png',
    alt: 'Mastercard',
    methods: ['CARD', 'MASTERCARD'],
  },
  amex: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765642/surfboardmerchant/ou2r1voubvuwizffjeec.png',
    alt: 'American Express',
    methods: ['AMEX'],
  },
  vipps: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765541/surfboardmerchant/b1x670kyqav9izugemri.png',
    alt: 'Vipps',
    methods: ['VIPPS', 'SVIPPS'],
  },
  klarna: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765566/surfboardmerchant/yksx3mcpjg7q0zaina7d.png',
    alt: 'Klarna',
    methods: ['KLARNA'],
  },
  applePay: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765618/surfboardmerchant/mh5toqaql7cgorpv4joc.png',
    alt: 'Apple Pay',
    methods: ['CARD', 'APPLE_PAY', 'APPLEPAY'],
  },
  googlePay: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765606/surfboardmerchant/jh6lnsjw78phfwcufxdl.png',
    alt: 'Google Pay',
    methods: ['CARD', 'GOOGLE_PAY', 'GOOGLEPAY'],
  },
  swish: {
    url: 'https://www.surfboardpayments.com/img/methods/swish.svg',
    alt: 'Swish',
    methods: ['SWISH', 'SSWISH'],
  },
};

// Order of display (correct order as specified)
const LOGO_ORDER = ['visa', 'mastercard', 'amex', 'vipps', 'klarna', 'applePay', 'googlePay', 'swish'];

export function OrderSummary() {
  const cart = useCheckoutStore((s) => s.cart);
  const merchant = useCheckoutStore((s) => s.merchant);
  const selectedShipping = useCheckoutStore((s) => s.selectedShipping);
  const totalWithShipping = useTotalWithShipping();
  const [showDetails, setShowDetails] = useState(false);

  const formatPrice = (amount: number, currency: string = 'NOK') => {
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount / 100);
  };

  const total = totalWithShipping || cart?.totalAmount || { amount: 0, currency: 'NOK' };
  
  // Get activated payment methods from merchant config
  const activatedMethods = merchant?.activatedPaymentMethods || ['CARD', 'VIPPS'];

  // Check if a payment logo should be shown based on activated methods
  const isMethodActivated = (logoKey: string): boolean => {
    const logoConfig = PAYMENT_METHOD_LOGOS[logoKey];
    if (!logoConfig) return false;
    return logoConfig.methods.some((method) => activatedMethods.includes(method));
  };

  // Get list of logos to display
  const logosToDisplay = LOGO_ORDER.filter(isMethodActivated);

  return (
    <div className={styles.summary}>
      <div className={styles.container}>
        <div className={styles.row}>
          <div>
            <div className={styles.label}>Ordrebeløp</div>
            <div className={styles.sublabel}>Inkl. MVA & frakt</div>
          </div>
          <div>
            <div className={styles.amount}>{formatPrice(total.amount, total.currency)}</div>
            <button 
              className={styles.detailsLink}
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? 'Skjul detaljer' : 'Vis detaljer'}
            </button>
          </div>
        </div>

        <div className={`${styles.details} ${!showDetails ? styles.detailsHidden : ''}`}>
          {cart?.items.map((item) => (
            <div key={item.id} className={styles.lineItem}>
              <div className={styles.itemName}>
                <span>{item.name}</span>
                <span className={styles.itemQty}>×{item.quantity}</span>
              </div>
              <span>{formatPrice(item.unitPrice.amount * item.quantity, item.unitPrice.currency)}</span>
            </div>
          ))}
          
          {selectedShipping && (
            <>
              <div className={styles.divider} />
              <div className={styles.lineItem}>
                <span>Frakt ({selectedShipping.displayName})</span>
                <span>
                  {selectedShipping.price.amount === 0 
                    ? 'Gratis' 
                    : formatPrice(selectedShipping.price.amount, selectedShipping.price.currency)
                  }
                </span>
              </div>
            </>
          )}
        </div>

        <div className={styles.paymentIcons}>
          {logosToDisplay.map((logoKey) => {
            const logo = PAYMENT_METHOD_LOGOS[logoKey];
            return (
              <img
                key={logoKey}
                src={logo.url}
                alt={logo.alt}
                className={styles.paymentIcon}
              />
            );
          })}
        </div>

        <div className={styles.poweredBy}>
          <span>Powered by</span>
          <img 
            src="https://res.cloudinary.com/martinsurf/image/upload/v1763562569/surfboardmerchant/cexdplrnd3tr54xhjjvt.png" 
            alt="Saga" 
            className={styles.sagaLogo}
          />
        </div>
      </div>
    </div>
  );
}
